import React, { useState, useEffect } from 'react';
import { contentService, ContentData } from '../services/contentService';
import contentData from '../data/content.json';

export const AdminPanel: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [content, setContent] = useState<ContentData>(contentData);
  const [editingPath, setEditingPath] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['hero']));

  const ADMIN_PASSWORD = 'holis2024';

  useEffect(() => {
    const savedAuth = sessionStorage.getItem('adminAuth');
    if (savedAuth === 'true') {
      setIsAuthenticated(true);
    }
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const data = await contentService.getContent();
      setContent(data);
    } catch (error) {
      console.error('Failed to load content:', error);
      setContent(contentData);
    }
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuth', 'true');
    } else {
      alert('Incorrect password');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuth');
    setPassword('');
  };

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const startEdit = (path: string, value: any) => {
    setEditingPath(path);
    setEditValue(typeof value === 'string' ? value : JSON.stringify(value, null, 2));
  };

  const saveEdit = async () => {
    if (!editingPath) return;

    const pathParts = editingPath.split('.');
    const newContent = JSON.parse(JSON.stringify(content));
    
    let current: any = newContent;
    for (let i = 0; i < pathParts.length - 1; i++) {
      current = current[pathParts[i]];
    }
    
    try {
      current[pathParts[pathParts.length - 1]] = editValue;
    } catch (e) {
      alert('Invalid value');
      return;
    }

    try {
      await contentService.saveContent(newContent);
      setContent(newContent);
      setEditingPath(null);
      setSaveMessage('✅ Changes saved to database and will appear on the website within seconds!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Failed to save content:', error);
      alert('Failed to save changes. Please check your Supabase connection.');
    }
  };

  const resetToDefault = async () => {
    if (window.confirm('Reset all content to default? This will remove all your changes.')) {
      try {
        await contentService.resetToDefault();
        setContent(contentData);
        setSaveMessage('✅ Content reset to default!');
        setTimeout(() => setSaveMessage(''), 3000);
      } catch (error) {
        console.error('Failed to reset content:', error);
        alert('Failed to reset content. Please check your Supabase connection.');
      }
    }
  };

  const cancelEdit = () => {
    setEditingPath(null);
    setEditValue('');
  };

  const renderValue = (value: any, path: string, depth: number = 0): React.JSX.Element => {
    if (typeof value === 'string') {
      const isEditing = editingPath === path;
      const isMultiline = value.includes('\n') || value.length > 60;

      return (
        <div className="mb-3 pl-4 border-l-2 border-bronze/20">
          <div className="flex items-start justify-between gap-2 mb-1">
            <span className="text-xs text-taupe font-mono">{path.split('.').pop()}</span>
            {!isEditing && (
              <button
                onClick={() => startEdit(path, value)}
                className="text-xs text-bronze hover:text-charcoal transition-colors"
              >
                Edit
              </button>
            )}
          </div>
          {isEditing ? (
            <div className="space-y-2">
              {isMultiline ? (
                <textarea
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="w-full px-3 py-2 bg-ivory border border-bronze/30 rounded text-sm font-cormorant focus:outline-none focus:border-bronze"
                  rows={Math.min(value.split('\n').length + 2, 10)}
                />
              ) : (
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="w-full px-3 py-2 bg-ivory border border-bronze/30 rounded text-sm font-cormorant focus:outline-none focus:border-bronze"
                />
              )}
              <div className="flex gap-2">
                <button
                  onClick={saveEdit}
                  className="px-3 py-1 bg-bronze text-ivory rounded text-sm hover:bg-charcoal transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={cancelEdit}
                  className="px-3 py-1 bg-taupe/20 text-charcoal rounded text-sm hover:bg-taupe/40 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-charcoal whitespace-pre-wrap bg-sand/30 p-2 rounded">
              {value}
            </p>
          )}
        </div>
      );
    }

    if (Array.isArray(value)) {
      return (
        <div className="ml-4 space-y-2">
          {value.map((item, index) => (
            <div key={index} className="border-l-2 border-taupe/20 pl-3">
              <span className="text-xs text-taupe font-mono">Item {index + 1}</span>
              {renderValue(item, `${path}.${index}`, depth + 1)}
            </div>
          ))}
        </div>
      );
    }

    if (typeof value === 'object' && value !== null) {
      return (
        <div className="ml-4 space-y-3">
          {Object.entries(value).map(([key, val]) => (
            <div key={key}>
              {renderValue(val, `${path}.${key}`, depth + 1)}
            </div>
          ))}
        </div>
      );
    }

    return <span className="text-sm">{String(value)}</span>;
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center p-4">
        <div className="bg-sand/50 p-8 rounded-lg shadow-lg max-w-md w-full">
          <h1 className="font-cormorant text-3xl text-center mb-6">Admin Panel</h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm mb-2 text-taupe">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-ivory border border-taupe/20 rounded-md focus:outline-none focus:border-bronze transition-colors"
                placeholder="Enter admin password"
              />
            </div>
            <button
              type="submit"
              className="w-full px-6 py-3 bg-bronze text-ivory rounded-md hover:bg-charcoal transition-colors font-medium"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory">
      <div className="sticky top-0 bg-sand/95 backdrop-blur-md border-b border-taupe/20 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="font-cormorant text-2xl">Content Editor</h1>
            <div className="flex gap-3">
              <button
                onClick={resetToDefault}
                className="px-4 py-2 text-sm text-taupe hover:text-bronze transition-colors border border-taupe/20 rounded"
              >
                Reset to Default
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm text-taupe hover:text-charcoal transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
          {saveMessage && (
            <div className="mt-2 p-3 bg-bronze/10 border border-bronze/30 rounded text-sm text-charcoal">
              {saveMessage}
            </div>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8 p-4 bg-sand/30 rounded-lg">
          <p className="text-sm text-taupe mb-2">
            <strong>✨ Instructions:</strong> Click "Edit" on any text field, make your changes, and click "Save". 
            Changes are saved to <strong>Supabase database</strong> and will <strong>automatically appear on the website</strong> within 5 seconds!
          </p>
          <p className="text-xs text-taupe mt-2">
            💡 Tip: Use the "Reset to Default" button above to restore original content if needed.
          </p>
        </div>

        <div className="space-y-4">
          {Object.entries(content).map(([section, sectionContent]) => (
            <div key={section} className="bg-sand/20 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection(section)}
                className="w-full px-6 py-4 flex justify-between items-center hover:bg-sand/40 transition-colors"
              >
                <h2 className="font-cormorant text-xl capitalize">{section}</h2>
                <svg
                  className={`w-5 h-5 transition-transform ${expandedSections.has(section) ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {expandedSections.has(section) && (
                <div className="px-6 py-4 border-t border-taupe/10">
                  {renderValue(sectionContent, section)}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 p-6 bg-charcoal/5 rounded-lg">
          <h3 className="font-cormorant text-xl mb-4">Current Content (JSON)</h3>
          <p className="text-sm text-taupe mb-3">This is automatically saved to Supabase. For backup purposes only:</p>
          <div className="relative">
            <pre className="bg-ivory p-4 rounded text-xs overflow-x-auto max-h-96 overflow-y-auto border border-taupe/20">
              {JSON.stringify(content, null, 2)}
            </pre>
            <button
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(content, null, 2));
                alert('JSON copied to clipboard!');
              }}
              className="absolute top-2 right-2 px-3 py-1 bg-bronze text-ivory text-xs rounded hover:bg-charcoal transition-colors"
            >
              Copy
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
